import { arrange, DASHBOARD_SETTING, type Arrangement } from '@nexus/core';
import {
  EmptyState,
  ErrorBoundary,
  ErrorState,
  PageHeader,
  Tile,
  TileGrid,
  TileSkeleton,
} from '@nexus/ui';
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { Suspense, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomizeDashboard } from '../dashboard/customize-dashboard';
import { ModuleApps } from '../dashboard/module-apps';
import { availableTiles, type DashboardTile } from '../dashboard/tiles';
import { useModules } from '../modules/context';
import { useSession } from '../session/session';
import { useSetting } from '../session/settings';

const DEFAULT_LAYOUT: Arrangement = { order: [], hidden: [] };

/**
 * Frame of one tile. Loading, failing and retrying stay inside the tile, so a broken tile of
 * one module never takes the start page down (docs/architecture/dashboard.md).
 */
function TileFrame({ tile }: { tile: DashboardTile }) {
  const { t } = useTranslation();
  const inset = tile.flush ? 'px-4' : undefined;
  return (
    <Tile
      title={t(tile.title)}
      icon={tile.icon}
      href={tile.path}
      size={tile.size}
      accent={tile.accent}
      flush={tile.flush}
    >
      <QueryErrorResetBoundary>
        {({ reset }) => (
          <ErrorBoundary
            onReset={reset}
            fallback={({ reset: retry }) => (
              <ErrorState compact title={t('home.tileFailed')} onRetry={retry} />
            )}
          >
            <Suspense
              fallback={
                <div className={inset}>
                  <TileSkeleton />
                </div>
              }
            >
              <tile.Content />
            </Suspense>
          </ErrorBoundary>
        )}
      </QueryErrorResetBoundary>
    </Tile>
  );
}

/**
 * Start page: the modules as app icons in the top row, then tiles of the kernel and the modules
 * the person may see, in the order the person chose (setting `core.dashboard`). Work, not welcome
 * (staw-design).
 */
export function HomePage() {
  const { t } = useTranslation();
  const session = useSession();
  const modules = useModules();
  const layout = useSetting<Arrangement>(DASHBOARD_SETTING) ?? DEFAULT_LAYOUT;
  const tiles = useMemo(() => availableTiles(modules, session), [modules, session]);
  const { visible } = arrange(tiles, layout);

  return (
    <>
      <PageHeader
        title={t('home.title')}
        description={session.company?.name}
        actions={<CustomizeDashboard tiles={tiles} layout={layout} />}
      />
      <ModuleApps />
      {visible.length > 0 ? (
        <TileGrid>
          {visible.map((tile) => (
            <TileFrame key={tile.key} tile={tile} />
          ))}
        </TileGrid>
      ) : (
        <div className="rounded-lg border border-dashed border-line-strong">
          <EmptyState
            icon="layout-dashboard"
            title={t('home.allHiddenTitle')}
            description={t('home.allHiddenDescription')}
          />
        </div>
      )}
    </>
  );
}
